const TARGET_LOCALE = 'en-GB';

const isDefaultLocale = (locale) =>
  locale === undefined || locale === null || locale === 'en-US' || locale === 'default';

const NUMERIC_DATE_ONLY = { day: '2-digit', month: '2-digit', year: 'numeric' };

const hasTimePart = (opts) =>
  !!opts && (opts?.hour || opts?.minute || opts?.second || opts?.timeStyle || opts?.hour12 !== undefined);

const hasDatePart = (opts) =>
  !!opts && (opts?.day || opts?.month || opts?.year || opts?.weekday || opts?.dateStyle);

const isMonthOnly = (opts) =>
  !!opts && opts?.month && !opts?.day && !opts?.year;

const isDayMonthNoYear = (opts) =>
  !!opts && opts?.day && opts?.month && !opts?.year;

// Date-only values (e.g. "2026-10-19" from a Postgres `date` column) are parsed by
// `new Date()` as UTC midnight. Formatting them in a negative-offset timezone shows
// the previous day. When a date is exactly UTC midnight and the caller only wants a
// date (no time part), format it in UTC so the calendar day stays correct.
const isUtcMidnight = (d) =>
  d instanceof Date &&
  !isNaN(d?.getTime()) &&
  d?.getUTCHours() === 0 &&
  d?.getUTCMinutes() === 0 &&
  d?.getUTCSeconds() === 0 &&
  d?.getUTCMilliseconds() === 0;

const withUtcIfDateOnly = (d, opts) => {
  if (!isUtcMidnight(d)) return opts;
  if (hasTimePart(opts)) return opts;
  if (opts?.timeZone) return opts;
  return { ...(opts || {}), timeZone: 'UTC' };
};

const normalizeDateOptions = (opts) => {
  if (!opts) return NUMERIC_DATE_ONLY;
  if (opts?.weekday || isMonthOnly(opts) || (opts?.month && !opts?.day && opts?.year)) return opts;
  if (isDayMonthNoYear(opts)) return { day: '2-digit', month: '2-digit' };
  if (opts?.day && opts?.month && opts?.year) {
    return { ...opts, day: '2-digit', month: '2-digit', year: 'numeric' };
  }
  if (opts?.dateStyle) {
    return { ...opts, dateStyle: undefined, ...NUMERIC_DATE_ONLY, ...(opts?.timeStyle ? { timeStyle: opts?.timeStyle } : {}) };
  }
  return opts;
};

if (typeof Date !== 'undefined' && !Date.prototype?.__ddmmyyyyPatched) {
  const origDate = Date.prototype?.toLocaleDateString;
  const origString = Date.prototype?.toLocaleString;

  Date.prototype.toLocaleDateString = function (locale, opts) {
    if (!isDefaultLocale(locale)) {
      return origDate?.call(this, locale, withUtcIfDateOnly(this, opts));
    }
    if (opts && hasTimePart(opts)) {
      return origDate?.call(this, TARGET_LOCALE, { ...opts, day: '2-digit', month: '2-digit', year: 'numeric', hour12: false });
    }
    return origDate?.call(this, TARGET_LOCALE, withUtcIfDateOnly(this, normalizeDateOptions(opts)));
  };

  Date.prototype.toLocaleString = function (locale, opts) {
    if (!isDefaultLocale(locale) && locale !== undefined) {
      if (locale !== 'en-US') return origString?.call(this, locale, withUtcIfDateOnly(this, opts));
    }
    if (locale === 'default' && opts && isMonthOnly(opts)) {
      return origString?.call(this, 'en-US', opts);
    }
    if (!opts) {
      return origString?.call(this, TARGET_LOCALE, {
        ...NUMERIC_DATE_ONLY,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    }
    if (hasDatePart(opts) && !hasTimePart(opts) && !opts?.timeStyle) {
      return origString?.call(this, TARGET_LOCALE, withUtcIfDateOnly(this, normalizeDateOptions(opts)));
    }
    if (opts?.dateStyle || opts?.timeStyle) {
      return origString?.call(this, TARGET_LOCALE, {
        ...NUMERIC_DATE_ONLY,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }
    return origString?.call(this, TARGET_LOCALE, opts);
  };

  Object.defineProperty(Date.prototype, '__ddmmyyyyPatched', { value: true, enumerable: false });
}

export const formatDateDMY = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const m = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m?.[3]}/${m?.[2]}/${m?.[1]}`;
  }
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d?.getTime())) return '';
  return d?.toLocaleDateString(TARGET_LOCALE, NUMERIC_DATE_ONLY);
};

export default formatDateDMY;
